import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../context/LocaleContext';
import { resolveSettingMediaUrl, useSiteSettings } from '../context/SiteSettingsContext';
import { resolveServiceMediaUrl } from '../utils/serviceContent';

const toPlainText = (value) => String(value || '')
  .replace(/<[^>]*>/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const toAbsoluteUrl = (value, origin) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  try {
    return new URL(raw, `${origin}/`).toString();
  } catch (_) {
    return raw;
  }
};

const setMeta = (key, value, attribute = 'name') => {
  if (!value) return;
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', value);
  element.dataset.serviceSeo = 'true';
};

const removeServiceJsonLd = () => {
  document.head.querySelectorAll(
    'script[data-service-jsonld="true"], script[data-server-service-jsonld="true"]'
  ).forEach((node) => node.remove());
};

const ServiceSeo = ({ service = null, landing = null }) => {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { settings } = useSiteSettings();

  const seo = useMemo(() => {
    if (!service?.title || !service?.slug) return null;

    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const siteTitle = settings.general.siteTitle || 'Haryad';
    const fallbackImage = resolveSettingMediaUrl(
      settings.seo.defaultShareImage || settings.general.logoUrl || '/logo/logo.webp'
    );
    const entity = landing || service;
    const title = String(entity.seoTitle || `${entity.title} | ${siteTitle}`).trim();
    const description = toPlainText(
      entity.seoDescription || entity.description || service.cardDescription || service.description
    ).slice(0, 320);
    const serviceUrl = `${origin}/${locale.code}/services/${encodeURIComponent(service.slug)}`;
    const url = landing
      ? `${serviceUrl}/${encodeURIComponent(landing.slug)}`
      : serviceUrl;
    const rawImage = landing?.image || service.image
      ? resolveServiceMediaUrl(landing?.image || service.image)
      : fallbackImage;
    const image = toAbsoluteUrl(rawImage, origin);
    const structuredImages = [
      image,
      ...(landing?.gallery || []).map((item) => (
        toAbsoluteUrl(resolveServiceMediaUrl(item?.image), origin)
      )),
      ...(landing?.highlightDetails || []).map((item) => (
        toAbsoluteUrl(resolveServiceMediaUrl(item?.image), origin)
      )),
    ].filter(Boolean);
    const serviceId = `${url}#service`;
    const breadcrumbId = `${url}#breadcrumb`;
    const socialUrls = Object.values(settings.contact.social || {}).filter(Boolean);

    return {
      title,
      description,
      url,
      image,
      jsonLd: {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'Organization',
            '@id': `${origin}/#organization`,
            name: siteTitle,
            url: `${origin}/`,
            ...(settings.contact.supportEmail ? { email: settings.contact.supportEmail } : {}),
            ...(settings.contact.phone ? { telephone: settings.contact.phone } : {}),
            ...(socialUrls.length ? { sameAs: socialUrls } : {}),
            logo: toAbsoluteUrl(resolveSettingMediaUrl(settings.general.logoUrl || '/logo/logo.webp'), origin)
              ? {
                '@type': 'ImageObject',
                url: toAbsoluteUrl(resolveSettingMediaUrl(settings.general.logoUrl || '/logo/logo.webp'), origin),
              }
              : undefined,
          },
          {
            '@type': 'Service',
            '@id': serviceId,
            name: entity.title,
            description,
            url,
            serviceType: entity.title,
            provider: { '@id': `${origin}/#organization` },
            ...(landing ? { isRelatedTo: { '@id': `${serviceUrl}#service` } } : {}),
            ...(structuredImages.length ? { image: structuredImages } : {}),
          },
          {
            '@type': 'WebPage',
            '@id': `${url}#webpage`,
            url,
            name: title,
            description,
            inLanguage: locale.intlLocale || locale.code,
            mainEntity: { '@id': serviceId },
            breadcrumb: { '@id': breadcrumbId },
          },
          {
            '@type': 'BreadcrumbList',
            '@id': breadcrumbId,
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: t('nav.home'), item: `${origin}/${locale.code}` },
              { '@type': 'ListItem', position: 2, name: service.title, item: serviceUrl },
              ...(landing ? [
                { '@type': 'ListItem', position: 3, name: landing.title, item: url },
              ] : []),
            ],
          },
        ].filter(Boolean),
      },
    };
  }, [landing, locale.code, locale.intlLocale, service, settings, t]);

  useEffect(() => {
    if (!seo) return undefined;

    document.title = seo.title;
    setMeta('description', seo.description);
    setMeta('robots', 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1');
    setMeta('og:type', 'website', 'property');
    setMeta('og:title', seo.title, 'property');
    setMeta('og:description', seo.description, 'property');
    setMeta('og:url', seo.url, 'property');
    setMeta('og:locale', String(locale.intlLocale || locale.code).replace(/-/g, '_'), 'property');
    if (seo.image) setMeta('og:image', seo.image, 'property');
    setMeta('twitter:card', seo.image ? 'summary_large_image' : 'summary');
    setMeta('twitter:title', seo.title);
    setMeta('twitter:description', seo.description);
    if (seo.image) setMeta('twitter:image', seo.image);

    removeServiceJsonLd();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.dataset.serviceJsonld = 'true';
    script.text = JSON.stringify(seo.jsonLd).replace(/</g, '\\u003c');
    document.head.appendChild(script);

    return () => {
      removeServiceJsonLd();
      document.head.querySelectorAll('[data-service-seo="true"]').forEach((node) => {
        if (node.matches('meta[name="description"]')) {
          node.setAttribute('content', settings.seo.defaultDescription || '');
          delete node.dataset.serviceSeo;
          return;
        }
        if (node.matches('meta[property="og:title"]')) {
          node.setAttribute('content', settings.seo.defaultTitle || settings.general.siteTitle || 'Haryad');
          delete node.dataset.serviceSeo;
          return;
        }
        if (node.matches('meta[property="og:description"]')) {
          node.setAttribute('content', settings.seo.defaultDescription || '');
          delete node.dataset.serviceSeo;
          return;
        }
        if (node.matches('meta[property="og:image"]') && settings.seo.defaultShareImage) {
          node.setAttribute('content', toAbsoluteUrl(resolveSettingMediaUrl(settings.seo.defaultShareImage), window.location.origin));
          delete node.dataset.serviceSeo;
          return;
        }
        node.remove();
      });
    };
  }, [locale.code, locale.intlLocale, seo, settings]);

  return null;
};

export default ServiceSeo;
